import {
  Button,
  DeleteConfirmButton,
  ExclamationTriangleIcon,
  Flex,
  Panel,
  Tag,
  Text,
  TextField,
  toaster,
} from 'components'
import { ExternalClientAppType } from 'types'
import { deleteExternalClient } from 'utils'
import { getExternalClientTypeText } from '../../utils'

interface Props {
  appTitle: string
  appType: ExternalClientAppType
  clientId: string
  clientSecret: string
  onClose: () => void
}

export default function ApplicationCredentials({
  appTitle,
  appType,
  clientId,
  clientSecret,
  onClose,
}: Props) {
  async function handleDelete() {
    await deleteExternalClient({ id: clientId })
    onClose()
  }

  function handleCopy(value: string, type: 'clientId' | 'clientSecret') {
    const contentType = type === 'clientId' ? 'Client ID' : 'Client Secret'
    navigator.clipboard
      .writeText(value)
      .then(() => {
        toaster.create({ type: 'success', description: `${contentType} copied to clipboard!` })
      })
      .catch(() => {
        toaster.create({ type: 'error', description: `Error copying ${contentType}` })
      })
  }

  return (
    <Panel p={7} gap={5}>
      <Flex gap={3} align="center">
        <Text textStyle="bodyMedium">{appTitle}</Text>
        <Tag h="30px" p={2} borderRadius="md" bg="primary.500" color="white">
          {getExternalClientTypeText(appType)}
        </Tag>
      </Flex>
      <Panel p={3}>
        <Flex gap={1} align="center">
          <ExclamationTriangleIcon type="outline" color="yellow.500" />
          <Text textStyle="bodySmall">
            Please save your Client ID and Client Secret now. For security reasons, these details
            will not be accessible once you leave this screen.
          </Text>
        </Flex>
      </Panel>

      <Flex direction="column" gap={2} maxW="600px">
        <Flex gap={3} align="flex-end">
          <TextField label="Client ID" value={clientId} readOnly />
          <Button variant="ghost" onClick={() => handleCopy(clientId, 'clientId')}>
            Copy
          </Button>
        </Flex>
        <Flex gap={3} align="flex-end">
          <TextField label="Client Secret" type="password" value={clientSecret} readOnly />
          <Button variant="ghost" onClick={() => handleCopy(clientSecret, 'clientSecret')}>
            Copy
          </Button>
        </Flex>
      </Flex>
      <Flex gap={5} justify="flex-end" align="center">
        <DeleteConfirmButton
          h="40px"
          _hover={{
            fontWeight: 'semibold',
          }}
          variant="solid"
          buttonLabel="Revoke"
          description='Are you sure you want to revoke this API key? Any applications using it will immediately lose access. Type "Revoke" if you are certain you want to revoke this application key'
          onDelete={handleDelete}
        />
        <Button palette="primary" onClick={onClose}>
          Done
        </Button>
      </Flex>
    </Panel>
  )
}
